import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { SearchComponent } from '../../../../../../_shared/components/filters/search/search.component';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { LookupPartReturnReason, LookupWorkOrder } from '../../../../../_shared/models/lookup.model';
import { Option } from '../../../models/option.model';
import { PartDetails } from '../../../models/part-details.model';
import { ReturnPartRequest } from '../../../models/return-part-request.model';
import { WorkOrderAssignedTechnician } from '../../../models/work-order-assigned-technician.model';
import { WorkOrderIssuedPart } from '../../../models/work-order-issued-part.model';
import { PartsAndInventoryService } from '../../../services/parts-and-inventory.service';

@Component({
  selector: 'app-return-parts',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, FlatpickrDatePickerComponent, SearchComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './return-parts.component.html',
  styleUrl: './return-parts.component.scss'
})
export class ReturnPartsComponent implements OnInit, OnDestroy {
  @Input() partId: number | null = null;

  form: FormGroup;
  isLoading = false;
  isPartLoading = false;
  isWorkOrderSearchLoading = false;
  isIssuedPartsLoading = false;
  isTechniciansLoading = false;
  isReturnReasonsLoading = false;

  workOrders: LookupWorkOrder[] = [];
  workOrderSearchTerm = '';
  selectedWorkOrder: LookupWorkOrder | null = null;
  issuedParts: WorkOrderIssuedPart[] = [];
  selectedPart: WorkOrderIssuedPart | null = null;
  assignedTechnicians: WorkOrderAssignedTechnician[] = [];
  selectedTechnician: WorkOrderAssignedTechnician | null = null;
  returnReasons: LookupPartReturnReason[] = [];
  selectedReturnReason: LookupPartReturnReason | null = null;
  private partDetails: PartDetails | null = null;

  quantityOptions: Option[] = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '5', value: '5' },
    { label: '10', value: '10' }
  ];

  private partsAndInventoryService = inject(PartsAndInventoryService);
  private lookupService = inject(LookupService);
  private errorService = inject(ErrorHandlerService);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);
  private readonly destroySubject = new Subject<void>();
  private readonly workOrderSearchSubject = new Subject<void>();
  private readonly workOrderDetailsSubject = new Subject<void>();

  get isReturnWorkPartOrderMode(): boolean {
    return this.partId === null;
  }

  get isInventoryReturnMode(): boolean {
    return !this.isReturnWorkPartOrderMode;
  }

  get isReturnedByRequired(): boolean {
    return this.isReturnWorkPartOrderMode;
  }

  constructor() {
    this.form = this.fb.group({
      workOrder: ['', Validators.required],
      partName: [{ value: '', disabled: true }, Validators.required],
      partNumber: [{ value: '', disabled: true }, Validators.required],
      description: [{ value: '', disabled: true }],
      date: ['', Validators.required],
      returnedBy: ['', Validators.required],
      quantityReturning: ['1', [Validators.required, Validators.min(1)]],
      returnReasonId: ['', Validators.required],
      notes: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.form.patchValue({
      date: this.getTodayIsoDate(),
      quantityReturning: '1'
    });
    this.configureModeValidators();
    void this.loadReturnReasons();

    if (this.isInventoryReturnMode) {
      void this.initializeInventoryMode();
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.workOrderSearchSubject.next();
    this.workOrderSearchSubject.complete();
    this.workOrderDetailsSubject.next();
    this.workOrderDetailsSubject.complete();
  }

  async onSearchTermUpdate(workOrderNumber: string): Promise<void> {
    if (this.isInventoryReturnMode) {
      return;
    }

    this.workOrderSearchSubject.next();
    this.workOrderSearchTerm = workOrderNumber;
    this.clearWorkOrderSelection();

    const searchTerm = workOrderNumber?.trim();
    if (!searchTerm) {
      this.workOrders = [];
      return;
    }

    this.isWorkOrderSearchLoading = true;
    try {
      const response = await this.lookupService.getWorkOrdersAsync(searchTerm, this.workOrderSearchSubject);
      this.workOrders = response?.data ?? [];

      if (!this.workOrders.length) {
        this.toastr.warning(this.transloco.translate('parts.modals.returnParts.toastNoWorkOrders'));
      }
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isWorkOrderSearchLoading = false;
    }
  }

  async onSelectWorkOrder(workOrder: LookupWorkOrder, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isInventoryReturnMode) {
      return;
    }

    this.workOrderDetailsSubject.next();
    this.selectedWorkOrder = workOrder;
    this.workOrderSearchTerm = workOrder.name;
    this.workOrders = [];
    this.form.get('workOrder')?.setValue(workOrder.name);
    this.form.get('workOrder')?.markAsTouched();
    this.resetIssuedPartSelection();
    this.resetTechnicianSelection();

    await Promise.all([
      this.loadIssuedParts(workOrder.id),
      this.loadAssignedTechnicians(workOrder.id)
    ]);
  }

  onSelectPart(part: WorkOrderIssuedPart, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedPart = part;
    this.form.patchValue({
      partName: part.partName,
      partNumber: part.partNumber
    });
  }

  onSelectTechnician(technician: WorkOrderAssignedTechnician, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedTechnician = technician;
    this.form.get('returnedBy')?.setValue(technician.id);
    this.form.get('returnedBy')?.markAsTouched();
  }

  onSelectReturnReason(reason: LookupPartReturnReason, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedReturnReason = reason;
    this.form.get('returnReasonId')?.setValue(reason.id);
    this.form.get('returnReasonId')?.markAsTouched();
  }

  onDateChange(value: string | undefined): void {
    this.form.get('date')?.setValue(value ?? '');
    this.form.get('date')?.markAsTouched();
  }

  onSelect(field: string, value: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.form.get(field)?.setValue(value);
    this.form.get(field)?.markAsTouched();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || this.isLoading || this.isPartLoading || this.isWorkOrderSearchLoading || this.isIssuedPartsLoading || this.isTechniciansLoading || this.isReturnReasonsLoading) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
      return;
    }

    if (this.isReturnWorkPartOrderMode && !this.selectedWorkOrder) {
      this.toastr.warning(this.transloco.translate('parts.modals.returnParts.toastSelectWorkOrder'));
      return;
    }

    if (!this.selectedPart) {
      this.toastr.warning(this.transloco.translate('parts.modals.returnParts.toastSelectPart'));
      return;
    }

    if (this.isReturnedByRequired && !this.selectedTechnician) {
      this.toastr.warning(this.transloco.translate('parts.modals.returnParts.toastSelectReturnedBy'));
      return;
    }

    if (!this.selectedReturnReason) {
      this.toastr.warning(this.transloco.translate('parts.modals.returnParts.toastSelectReason'));
      return;
    }

    this.isLoading = true;
    try {
      const request = this.buildReturnPartRequest();
      await this.partsAndInventoryService.returnPartAsync(request);
      this.toastr.success(this.transloco.translate('parts.modals.returnParts.toastReturned'));
      this.activeModal.close(true);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadReturnReasons(): Promise<void> {
    this.isReturnReasonsLoading = true;
    try {
      const response = await this.lookupService.getPartReturnReasonsAsync(this.destroySubject);
      this.returnReasons = response?.data ?? [];
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isReturnReasonsLoading = false;
    }
  }

  private async initializeInventoryMode(): Promise<void> {
    if (!Number.isFinite(this.partId)) {
      this.errorService.handleError('Invalid part id');
      this.onCancel();
      return;
    }

    await this.loadPartDetails();
  }

  private async loadPartDetails(): Promise<void> {
    this.isPartLoading = true;
    try {
      const response = await this.partsAndInventoryService.getPartByIdAsync(this.partId!);
      this.partDetails = response.data ?? null;
      this.form.patchValue({
        partName: this.partDetails?.partName ?? '',
        partNumber: this.partDetails?.partNumber ?? '',
        description: this.partDetails?.description ?? ''
      });
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isPartLoading = false;
    }
  }

  private async loadIssuedParts(workOrderId: number): Promise<void> {
    if (this.isInventoryReturnMode) {
      return;
    }

    this.isIssuedPartsLoading = true;
    try {
      const response = await this.partsAndInventoryService.getIssuedPartsByWorkOrderAsync(workOrderId, this.workOrderDetailsSubject);
      this.issuedParts = response?.data ?? [];

      if (!this.issuedParts.length) {
        this.toastr.warning(this.transloco.translate('parts.modals.returnParts.toastNoIssuedParts'));
        return;
      }

      const matchingPart = this.partId
        ? this.issuedParts.find(part => part.partId === this.partId)
        : null;

      if (matchingPart) {
        this.onSelectPart(matchingPart);
      }
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isIssuedPartsLoading = false;
    }
  }

  private async loadAssignedTechnicians(workOrderId: number): Promise<void> {
    if (this.isInventoryReturnMode) {
      return;
    }

    this.isTechniciansLoading = true;
    try {
      const response = await this.partsAndInventoryService.getWorkOrderAssignedTechniciansAsync(workOrderId, this.workOrderDetailsSubject);
      this.assignedTechnicians = response?.data ?? [];

      if (this.assignedTechnicians.length === 1) {
        this.onSelectTechnician(this.assignedTechnicians[0]);
      }
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isTechniciansLoading = false;
    }
  }

  private clearWorkOrderSelection(): void {
    this.selectedWorkOrder = null;
    this.workOrders = [];
    this.form.get('workOrder')?.setValue('');
    this.resetIssuedPartSelection();
    this.resetTechnicianSelection();
  }

  private resetIssuedPartSelection(): void {
    this.issuedParts = [];
    this.selectedPart = null;
    this.form.patchValue({
      partName: '',
      partNumber: ''
    });
  }

  private resetTechnicianSelection(): void {
    this.assignedTechnicians = [];
    this.selectedTechnician = null;
    this.form.get('returnedBy')?.setValue('');
  }

  private configureModeValidators(): void {
    if (this.isReturnWorkPartOrderMode) {
      this.form.get('workOrder')?.setValidators(Validators.required);
      this.form.get('returnedBy')?.setValidators(Validators.required);
      this.form.get('notes')?.setValidators(Validators.required);
    } else {
      this.form.get('workOrder')?.clearValidators();
      this.form.get('returnedBy')?.clearValidators();
      this.form.get('notes')?.clearValidators();
    }

    this.form.get('workOrder')?.updateValueAndValidity();
    this.form.get('returnedBy')?.updateValueAndValidity();
    this.form.get('notes')?.updateValueAndValidity();
  }

  private getTodayIsoDate(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private buildReturnPartRequest(): ReturnPartRequest {
    const request: ReturnPartRequest = {
      dateReturned: String(this.form.get('date')?.value ?? ''),
      quantityReturned: Number(this.form.get('quantityReturning')?.value ?? 0),
      returnReasonId: Number(this.form.get('returnReasonId')?.value),
      notes: this.toNullableString(this.form.get('notes')?.value),
      partRequestId: this.selectedPart!.requestId
    };

    if (this.selectedTechnician) {
      request.returnedBy = this.selectedTechnician.id;
    }

    return request;
  }

  private toNullableString(value: unknown): string | null {
    const parsed = String(value ?? '').trim();
    return parsed.length ? parsed : null;
  }
}
